import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, fmtDate, addDaysISO } from "@/lib/api";
import { TextField, TextAreaField, SelectField, ChipGroup, Field } from "@/components/Form";
import {
  WORKER_ROLES, SOFTWARE, LANGUAGES, EXPERIENCE, COVERAGE_TYPES, ENVIRONMENT,
  REQUIREMENTS_BY_ROLE, RESPONSIBILITIES, WEEKDAYS,
} from "@/constants/options";
import { X, Plus, AlertTriangle } from "lucide-react";
import { US_STATES } from "@/constants/states";
import { PhiWarning } from "@/components/Notices";

export default function PostShift() {
  const navigate = useNavigate();
  const [pharmacy, setPharmacy] = useState(null);
  const [form, setForm] = useState({
    worker_role: "Pharmacist",
    coverage_type: "one-time",
    start_time: "10:00",
    end_time: "18:00",
    pay_type: "exact",
    pay_rate: 75,
    pay_rate_max: "",
    address: "",
    city: "",
    state: "NY",
    zip_code: "",
    requirements: [],
    software_required: [],
    languages_required: [],
    experience_preferred: [],
    responsibilities: [],
    environment: [],
    notes: "",
    hide_pharmacy_name: false,
    recurring_weekday: 0,
    recurring_start: addDaysISO(1),
    recurring_end: addDaysISO(60),
  });
  const [dates, setDates] = useState([addDaysISO(1)]);
  const [newDate, setNewDate] = useState("");
  const [ack, setAck] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/pharmacy/profile");
      setPharmacy(data);
      if (data)
        setForm((f) => ({
          ...f,
          address: data.address || "",
          city: data.city || "",
          state: data.state || "NY",
          zip_code: data.zip_code || "",
          software_required: data.software || [],
        }));
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const multi = form.coverage_type === "multiple-dates";
  const recurring = form.coverage_type === "recurring";

  const addDate = () => {
    if (newDate && !dates.includes(newDate)) setDates([...dates, newDate].sort());
    setNewDate("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        confidentiality_ack: ack,
        pay_rate: form.pay_type === "negotiable" ? null : Number(form.pay_rate),
        pay_rate_max: form.pay_type === "range" && form.pay_rate_max ? Number(form.pay_rate_max) : null,
        dates: recurring ? [] : multi ? dates : [dates[0]],
        recurring_weekday: recurring ? Number(form.recurring_weekday) : null,
        recurring_start: recurring ? form.recurring_start : null,
        recurring_end: recurring ? form.recurring_end : null,
        location_label: `${form.city}, ${form.state}`,
      };
      const { data } = await api.post("/pharmacy/shifts", payload);
      navigate(data.count === 1 ? `/pharmacy/shifts/${data.shifts[0].shift_id}` : "/pharmacy");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <p className="overline">Post shift</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Post upcoming coverage</h1>
      <p className="mt-3 text-sm text-slate-600">
        Role, date, hours and pay are all you need. Everything else is optional and just improves matching.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="post-shift-form">
        <section className="card-surface space-y-5 p-6">
          <ChipGroup
            label="Role needed"
            options={WORKER_ROLES}
            value={[form.worker_role]}
            onChange={(v) => set("worker_role", v.find((x) => x !== form.worker_role) || form.worker_role)}
            testIdPrefix="shift-role"
          />
          <SelectField
            label="Coverage type"
            options={COVERAGE_TYPES}
            value={form.coverage_type}
            onChange={(e) => set("coverage_type", e.target.value)}
            testId="shift-coverage-type"
          />

          {!recurring && (
            <Field label={multi ? "Dates" : "Date"} hint={multi ? "Each date becomes its own shift workers can accept individually." : undefined}>
              <div className="space-y-3">
                {!multi ? (
                  <input
                    type="date"
                    className="field-input"
                    value={dates[0] || ""}
                    onChange={(e) => setDates([e.target.value])}
                    required
                    data-testid="shift-date"
                  />
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2" data-testid="shift-dates-list">
                      {dates.map((d) => (
                        <span key={d} className="pill border-brand/30 bg-brand-soft text-brand">
                          {fmtDate(d, { month: "short", day: "numeric", year: "numeric" })}
                          <button type="button" onClick={() => setDates(dates.filter((x) => x !== d))} data-testid={`remove-date-${d}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="date" className="field-input" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="shift-new-date" />
                      <button type="button" onClick={addDate} className="btn-ghost" data-testid="shift-add-date">
                        <Plus className="h-4 w-4" /> Add date
                      </button>
                    </div>
                  </>
                )}
              </div>
            </Field>
          )}

          {recurring && (
            <div className="grid gap-5 sm:grid-cols-3">
              <SelectField label="Every" options={WEEKDAYS} value={form.recurring_weekday} onChange={(e) => set("recurring_weekday", e.target.value)} testId="shift-weekday" />
              <TextField label="From" type="date" value={form.recurring_start} onChange={(e) => set("recurring_start", e.target.value)} testId="shift-recurring-start" />
              <TextField label="Until" type="date" value={form.recurring_end} onChange={(e) => set("recurring_end", e.target.value)} testId="shift-recurring-end" />
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Start time" type="time" required value={form.start_time} onChange={(e) => set("start_time", e.target.value)} testId="shift-start-time" />
            <TextField label="End time" type="time" required value={form.end_time} onChange={(e) => set("end_time", e.target.value)} testId="shift-end-time" />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <SelectField
              label="Pay type"
              options={[
                { value: "exact", label: "Exact rate" },
                { value: "range", label: "Rate range" },
                { value: "negotiable", label: "Negotiable" },
              ]}
              value={form.pay_type}
              onChange={(e) => set("pay_type", e.target.value)}
              testId="shift-pay-type"
            />
            {form.pay_type !== "negotiable" && (
              <TextField label="Hourly rate ($)" type="number" min="0" value={form.pay_rate} onChange={(e) => set("pay_rate", e.target.value)} testId="shift-pay-rate" />
            )}
            {form.pay_type === "range" && (
              <TextField label="Up to ($)" type="number" min="0" value={form.pay_rate_max} onChange={(e) => set("pay_rate_max", e.target.value)} testId="shift-pay-rate-max" />
            )}
          </div>
        </section>

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Location</h2>
          <p className="text-sm text-slate-500">Defaults to {pharmacy?.name || "your pharmacy"} address — edit if this shift is elsewhere.</p>
          <TextField label="Address" value={form.address} onChange={(e) => set("address", e.target.value)} testId="shift-address" />
          <div className="grid gap-5 sm:grid-cols-3">
            <TextField label="City / neighborhood" value={form.city} onChange={(e) => set("city", e.target.value)} testId="shift-city" />
            <SelectField label="State" options={US_STATES} value={form.state} onChange={(e) => set("state", e.target.value)} testId="shift-state" />
            <TextField label="ZIP" required value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} testId="shift-zip" />
          </div>
          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[#1E3A8A]"
              checked={!!form.hide_pharmacy_name}
              onChange={(e) => set("hide_pharmacy_name", e.target.checked)}
              data-testid="shift-hide-pharmacy-name"
            />
            <span>
              <strong>Hide mode for this shift</strong> — workers see only the city, ZIP and requirements.
              Your pharmacy name, address and contact details unlock once the shift is confirmed.
            </span>
          </label>
        </section>

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Requirements & skills</h2>
          <ChipGroup label={`${form.worker_role} requirements`} options={REQUIREMENTS_BY_ROLE[form.worker_role]} value={form.requirements} onChange={(v) => set("requirements", v)} testIdPrefix="shift-requirement" />
          <ChipGroup label="Software required" options={SOFTWARE} value={form.software_required} onChange={(v) => set("software_required", v)} testIdPrefix="shift-software" />
          <ChipGroup label="Languages preferred" options={LANGUAGES} value={form.languages_required} onChange={(v) => set("languages_required", v)} testIdPrefix="shift-language" />
          <ChipGroup label="Experience preferred" options={EXPERIENCE} value={form.experience_preferred} onChange={(v) => set("experience_preferred", v)} testIdPrefix="shift-experience" />
          <ChipGroup label="Responsibilities" options={RESPONSIBILITIES} value={form.responsibilities} onChange={(v) => set("responsibilities", v)} testIdPrefix="shift-responsibility" />
          <ChipGroup label="Pharmacy environment" options={ENVIRONMENT} value={form.environment} onChange={(v) => set("environment", v)} testIdPrefix="shift-environment" />
          <TextAreaField
            label="Additional notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Easy-going store. Pharmacy mainly needs prescription verification. Good tech support."
            testId="shift-notes"
          />
          <PhiWarning testId="shift-notes-phi-warning" />
        </section>

        <section className="card-surface space-y-4 border-amber-200 p-6" data-testid="confidentiality-section">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Important before you publish
          </h2>
          <p className="text-sm text-slate-600">Please do not include:</p>
          <ul className="grid gap-1.5 text-sm text-slate-600 sm:grid-cols-2">
            {[
              "Patient information",
              "PHI",
              "Prescription information",
              "Social Security numbers",
              "Passwords",
              "Banking information",
              "Confidential business information",
            ].map((i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                {i}
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-600">Use the platform only for staffing-related information.</p>
          <label className="flex cursor-pointer items-start gap-2.5 text-sm leading-relaxed text-slate-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[#1E3A8A]"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              data-testid="shift-confidentiality-ack"
            />
            I confirm that this shift description does not contain patient information, PHI, or unnecessary
            confidential information.
          </label>
        </section>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="post-shift-error">{error}</p>}

        <div className="sticky bottom-4 flex items-center gap-4 rounded-full border border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
          <button type="submit" disabled={busy || !ack} className="btn-primary text-base" data-testid="post-shift-submit">
            {busy ? "Posting…" : "Post shift"}
          </button>
          <span className="text-sm text-slate-500">
            {!ack
              ? "Confirm the confidentiality acknowledgement to post"
              : recurring
              ? "Creates one shift per recurring date"
              : `${multi ? dates.length : 1} shift(s) will be created`}
          </span>
        </div>
      </form>
    </div>
  );
}
